/**
 * Tenant staff invite records — SSOT: tenants.settings.staffInvites (Phase 3 scaffold)
 * @see identity-federation-design.md §4.3
 */

import { z } from 'zod';

export const TENANT_STAFF_INVITE_STATUSES = ['pending', 'accepted', 'revoked'] as const;
export type TenantStaffInviteStatus = (typeof TENANT_STAFF_INVITE_STATUSES)[number];

/** Phase 3 promote scaffold — role hint options for pending staff invites */
export const TENANT_STAFF_INVITE_ROLE_HINTS = ['owner', 'admin', 'staff'] as const;
export type TenantStaffInviteRoleHint = (typeof TENANT_STAFF_INVITE_ROLE_HINTS)[number];

export const tenantStaffInviteSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  status: z.enum(TENANT_STAFF_INVITE_STATUSES),
  invitedAt: z.string().datetime(),
  acceptedAt: z.string().datetime().optional(),
  linkedEntityId: z.string().min(1).optional(),
  roleHint: z.string().min(1).optional(),
  inviteToken: z.string().min(8),
  invitedByUserId: z.string().min(1).optional(),
});

/** Invite landing URL — `{tenantHost}/login?invite={token}` */
export function buildStaffInviteLoginPath(inviteToken: string): string {
  const token = inviteToken.trim();
  return `/login?invite=${encodeURIComponent(token)}`;
}

export type TenantStaffInvite = z.infer<typeof tenantStaffInviteSchema>;

export function parseTenantStaffInvites(raw: unknown): TenantStaffInvite[] {
  if (!Array.isArray(raw)) return [];
  const invites: TenantStaffInvite[] = [];
  for (const row of raw) {
    const parsed = tenantStaffInviteSchema.safeParse(row);
    if (parsed.success) invites.push(parsed.data);
  }
  return invites;
}
